/**
 * Driver Management API Hooks
 * React hooks for managing driver data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import * as React from 'react';
import { adminApiClient } from './adminApiClient';
import { DriverDTO } from './adminTypes';
import { ApiResponse, Pageable, PagedData } from './types';

/** Normalize driver stats from alternate API keys and flatten nested sub-objects */
function normalizeDriverStats(d: any): DriverDTO {
  const out = { ...d } as DriverDTO;

  // Flatten nested sub-objects the API may return
  const nested = ['vehicleDetails', 'bankingInfo', 'bankingAndPayoutInfo', 'kycDocuments', 'kycInfo', 'personalDetails'];
  for (const key of nested) {
    if (d[key] && typeof d[key] === 'object' && !Array.isArray(d[key])) {
      Object.assign(out, d[key]);
    }
  }

  // Alternative field names
  if (out.fullName == null && d.name != null) out.fullName = d.name;
  if (out.fullName == null || out.fullName.trim() === '') {
    const fn = (d as any).firstName || '';
    const ln = (d as any).lastName || '';
    if (fn || ln) out.fullName = `${fn} ${ln}`.trim();
  }
  if (out.totalRidesCompleted == null && d.totalRides != null) out.totalRidesCompleted = Number(d.totalRides);
  if (out.totalEarnings == null && d.earnings != null) out.totalEarnings = Number(d.earnings);
  if (out.driverRating == null && d.averageRating != null) out.driverRating = Number(d.averageRating);
  if (out.averageRating == null && d.driverRating != null) out.averageRating = Number(d.driverRating);
  if (out.vehicleRegistrationNumber == null && d.vehicleLicensePlate != null) out.vehicleRegistrationNumber = d.vehicleLicensePlate;
  if (out.yearOfManufacture == null && d.vehicleYear != null) out.yearOfManufacture = d.vehicleYear;
  if (out.accountNumber == null && d.bankAccountNumber != null) out.accountNumber = d.bankAccountNumber;
  if (out.ifscCode == null && d.bankIfscCode != null) out.ifscCode = d.bankIfscCode;
  if (out.ownershipDocUrl == null && d.vehicleRegistrationCertUrl != null) out.ownershipDocUrl = d.vehicleRegistrationCertUrl;
  if (out.insuranceDocUrl == null && d.vehicleInsuranceCertUrl != null) out.insuranceDocUrl = d.vehicleInsuranceCertUrl;

  // Normalize isActive to boolean
  if (out.isActive !== undefined && typeof out.isActive !== 'boolean') {
    const v = String(out.isActive).toLowerCase();
    out.isActive = v === 'true' || v === 'active' || v === '1';
  }

  if (out.kycStatus) {
    const normalized = normalizeKycStatusValue(out.kycStatus);
    if (normalized) out.kycStatus = normalized;
  }
  return out;
}

const normalizeKycStatusValue = (status?: string | null) => {
  if (!status) return status;
  const normalized = status.toString().toUpperCase().replace(/[\s-]+/g, '_');
  if (normalized === 'KIV') return 'KEEP_IN_VIEW';
  if (normalized === 'KEEPINVIEW') return 'KEEP_IN_VIEW';
  return normalized;
};

const toApiKycStatus = (status?: string | null) => {
  const normalized = normalizeKycStatusValue(status);
  if (!normalized) return normalized;
  if (normalized === 'KEEP_IN_VIEW') return 'KIV';
  return normalized;
};

/**
 * Hook to fetch a single driver by ID
 * @param id - Driver ID
 * @param options - Hook options
 */
export function useDriver(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: DriverDTO) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await adminApiClient.get<ApiResponse<DriverDTO>>(`/drivers/${id}`);

    // Handle Response wrapper
    if (response.data) {
      return normalizeDriverStats(response.data);
    }

    return normalizeDriverStats(response as any);
  }, [id]);

  const query = useQuery<DriverDTO>({
    queryKey: ['driver', id],
    queryFn,
    enabled: options.enabled !== false && !!id,
  });

  // Handle callbacks using useEffect pattern (React Query v5)
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return {
    ...query,
    data: query.data,
    loading: query.isLoading || query.isFetching,
    error: query.error,
  };
}

export function useDrivers(
  params: {
    page?: number;
    size?: number;
    sort?: string[];
    search?: string;
    status?: string;     // ACTIVE | INACTIVE | ONDUTY
    kycStatus?: string;  // APPROVED | PENDING | etc
  } = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<DriverDTO>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const endpoint = '/drivers/list';

    const queryParams: Record<string, any> = {};

    // ✅ Pagination
    if (params.page !== undefined) {
      queryParams.page = params.page;
    }

    if (params.size !== undefined) {
      queryParams.size = params.size;
    }

    // ✅ Sorting
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }

    // ✅ Search
    if (params.search && params.search.trim().length > 0) {
      queryParams.search = params.search;
    }

    // ✅ Status → maps to backend "filter"
    if (params.status) {
      queryParams.filter = params.status;
    }

    // ✅ KYC → maps to backend "kyc"
    if (params.kycStatus) {
      queryParams.kyc = params.kycStatus;
    }

    const response = await adminApiClient.get<ApiResponse<PagedData<DriverDTO>>>(
      endpoint,
      queryParams
    );

    const data = response.data;

    // ✅ Normalize safely
    return {
      ...data,
      content: (data?.content || []).map(normalizeDriverStats),
      page: data?.page || {
        size: params.size ?? 10,
        number: params.page ?? 0,
        totalElements: data?.content?.length ?? 0,
        totalPages: 1,
      },
    };
  }, [
    params.page,
    params.size,
    params.sort,
    params.search,
    params.status,
    params.kycStatus,
  ]);

  return useQuery<PagedData<DriverDTO>>({
    queryKey: ['drivers', params],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to create a new driver
 * @param options - Hook options
 */
export function useCreateDriver(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: DriverDTO) => void;
    onError?: (error: Error, variables: DriverDTO) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, DriverDTO>({
    mutationFn: async (driverData: DriverDTO) => {
      const response = await adminApiClient.post<ApiResponse<DriverDTO>>('/drivers', driverData);
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update an existing driver
 * @param options - Hook options
 */
export function useUpdateDriver(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: { id: number; data: DriverDTO }
    ) => void;
    onError?: (error: Error, variables: { id: number; data: DriverDTO }) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, { id: number; data: DriverDTO }>({
    mutationFn: async ({ id, data }: { id: number; data: DriverDTO }) => {
      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(`/drivers/${id}`, data);
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to delete a driver
 * @param options - Hook options
 */
export function useDeleteDriver(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: DeleteDriverVariables) => void;
    onError?: (error: Error, variables: DeleteDriverVariables) => void;
  } = {}
) {
  return useMutation<ApiResponse<void>, Error, DeleteDriverVariables>({
    mutationFn: async (variables: DeleteDriverVariables) => {
      // First delete the driver
      const response = await adminApiClient.delete<ApiResponse<void>>(`/drivers/${variables.driverId}`);

      // Then find and delete the corresponding user if email is provided
      if (variables.email) {
        try {
          const userResponse = await adminApiClient.get<any>(`/api/admin/user-by-email?email=${encodeURIComponent(variables.email)}`);
          if (userResponse.data?.id) {
            await adminApiClient.delete<ApiResponse<void>>(`/api/admin/${userResponse.data.id}`);
          }
        } catch (userError) {
          // Log the error but don't fail the entire operation
          console.warn('Failed to delete corresponding user:', userError);
        }
      }

      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Variables for delete driver operation
 */
export interface DeleteDriverVariables {
  driverId: number;
  email?: string;
  phoneNumber?: string;
}

/**
 * Hook to fetch drivers by KYC status
 * @param kycStatus - KYC status filter
 * @param options - Hook options
 */
export function useDriversByKycStatus(
  kycStatus?: string,
  options: {
    enabled?: boolean;
    onSuccess?: (data: DriverDTO[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};
    if (kycStatus) {
      queryParams.kycStatus = toApiKycStatus(kycStatus);
    }

    const response = await adminApiClient.get<ApiResponse<DriverDTO[]>>(
      '/drivers/drivers-list-by-kyc-status',
      queryParams
    );

    if (response.data) {
      return response.data;
    }

    return response as any;
  }, [kycStatus]);

  return useQuery<DriverDTO[]>({
    queryKey: ['drivers-by-kyc-status', kycStatus],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to toggle driver active status
 * @param options - Hook options
 */
export function useToggleDriverActiveStatus(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, number>({
    mutationFn: async (id: number) => {
      const response = await adminApiClient.get<ApiResponse<DriverDTO>>(
        `/drivers/toggle-active-status/${id}`
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to upload profile photo
 * @param options - Hook options
 */
export function useUploadProfilePhoto(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: { driverId: number; file: File }) => void;
    onError?: (error: Error, variables: { driverId: number; file: File }) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, { driverId: number; file: File }>({
    mutationFn: async ({ driverId, file }) => {
      const formData = new FormData();
      formData.append('profilePhotoUrl', file);

      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/upload-profile-photo/${driverId}`,
        formData
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to upload KYC documents
 * @param options - Hook options
 */
export function useUploadKycDocuments(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        govtIdType: string;
        govtIdNumber: string;
        files: {
          govtIdFrontUrl: File;
          govtIdBackUrl: File;
          drivingLicenseFrontUrl: File;
          drivingLicenseBackUrl: File;
          selfieWithIdUrl: File;
        };
      }
    ) => void;
    onError?: (
      error: Error,
      variables: {
        driverId: number;
        govtIdType: string;
        govtIdNumber: string;
        files: {
          govtIdFrontUrl: File;
          govtIdBackUrl: File;
          drivingLicenseFrontUrl: File;
          drivingLicenseBackUrl: File;
          selfieWithIdUrl: File;
        };
      }
    ) => void;
  } = {}
) {
  return useMutation<
    ApiResponse<DriverDTO>,
    Error,
    {
      driverId: number;
      govtIdType: string;
      govtIdNumber: string;
      files: {
        govtIdFrontUrl: File;
        govtIdBackUrl: File;
        drivingLicenseFrontUrl: File;
        drivingLicenseBackUrl: File;
        selfieWithIdUrl: File;
      };
    }
  >({
    mutationFn: async ({ driverId, govtIdType, govtIdNumber, files }) => {
      const formData = new FormData();
      formData.append('govtIdFrontUrl', files.govtIdFrontUrl);
      formData.append('govtIdBackUrl', files.govtIdBackUrl);
      formData.append('drivingLicenseFrontUrl', files.drivingLicenseFrontUrl);
      formData.append('drivingLicenseBackUrl', files.drivingLicenseBackUrl);
      formData.append('selfieWithIdUrl', files.selfieWithIdUrl);

      const queryParams = {
        govtIdType,
        govtIdNumber,
        driverId,
      };

      const response = await adminApiClient.post<ApiResponse<DriverDTO>>(
        '/drivers/upload-kyc-documents',
        formData,
        { params: queryParams }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update KYC documents (for edit mode)
 * Uses POST /drivers/upload-kyc-documents with query params
 */
export function useUpdateKycDocuments(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: any) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({ driverId, govtIdType, govtIdNumber, files }) => {
      const formData = new FormData();
      if (files.govtIdFrontUrl) formData.append('govtIdFrontUrl', files.govtIdFrontUrl);
      if (files.govtIdBackUrl) formData.append('govtIdBackUrl', files.govtIdBackUrl);
      if (files.drivingLicenseFrontUrl) formData.append('drivingLicenseFrontUrl', files.drivingLicenseFrontUrl);
      if (files.drivingLicenseBackUrl) formData.append('drivingLicenseBackUrl', files.drivingLicenseBackUrl);
      if (files.selfieWithIdUrl) formData.append('selfieWithIdUrl', files.selfieWithIdUrl);

      const response = await adminApiClient.post<ApiResponse<DriverDTO>>(
        '/drivers/upload-kyc-documents',
        formData,
        { params: { govtIdType, govtIdNumber, driverId } }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to upload vehicle details (POST - combines details + documents)
 * @param options - Hook options
 */
export function useUploadVehicleDetails(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        vehicleType: string;
        vehicleCategoryId: number;
        vehicleMake: string;
        vehicleModel: string;
        vehicleRegistrationNumber: string;
        yearOfManufacture: number;
        vehicleColor: string;
        seatingCapacity: number;
        luggageCapacity: number;
        files: {
          ownershipDocUrl: File;
          insuranceDocUrl: File;
          roadTaxDocUrl: File;
        };
      }
    ) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({
      driverId,
      vehicleType,
      vehicleCategoryId,
      vehicleMake,
      vehicleModel,
      vehicleRegistrationNumber,
      yearOfManufacture,
      vehicleColor,
      seatingCapacity,
      luggageCapacity,
      files,
    }) => {
      const formData = new FormData();
      formData.append('ownershipDocUrl', files.ownershipDocUrl || new Blob(), files.ownershipDocUrl?.name || '');
      formData.append('insuranceDocUrl', files.insuranceDocUrl || new Blob(), files.insuranceDocUrl?.name || '');
      formData.append('roadTaxDocUrl', files.roadTaxDocUrl || new Blob(), files.roadTaxDocUrl?.name || '');

      const queryParams = {
        driverId,
        vehicleType,
        vehicleCategoryId,
        vehicleMake,
        vehicleModel,
        vehicleRegistrationNumber,
        yearOfManufacture,
        vehicleColor,
        seatingCapacity,
        luggageCapacity,
      };

      const response = await adminApiClient.post<ApiResponse<DriverDTO>>(
        '/drivers/upload-vehicle-details',
        formData,
        { params: queryParams }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update vehicle details (PUT - details only, no documents)
 * @param options - Hook options
 */
export function useUpdateVehicleDetails(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        vehicleType: string;
        vehicleCategoryId: number;
        vehicleMake: string;
        vehicleModel: string;
        vehicleRegistrationNumber: string;
        yearOfManufacture: number;
        vehicleColor: string;
        seatingCapacity: number;
        luggageCapacity: number;
      }
    ) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({
      driverId,
      vehicleType,
      vehicleCategoryId,
      vehicleMake,
      vehicleModel,
      vehicleRegistrationNumber,
      yearOfManufacture,
      vehicleColor,
      seatingCapacity,
      luggageCapacity,
    }) => {
      const queryParams = {
        vehicleType,
        vehicleCategoryId,
        vehicleMake,
        vehicleModel,
        vehicleRegistrationNumber,
        yearOfManufacture,
        vehicleColor,
        seatingCapacity,
        luggageCapacity,
      };

      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/update-vehicle-details/${driverId}`,
        null,
        { params: queryParams }
      );
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['driver', variables.driverId] });
      queryClient.invalidateQueries({ queryKey: ['drivers'], exact: false });
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
}

/**
 * Hook to update vehicle documents
 * @param options - Hook options
 */
export function useUpdateVehicleDocuments(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        files: {
          ownershipDocUrl: File;
          insuranceDocUrl: File;
          roadTaxDocUrl: File;
        };
      }
    ) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({ driverId, files }) => {
      const formData = new FormData();
      formData.append('ownershipDocUrl', files.ownershipDocUrl || new Blob(), files.ownershipDocUrl?.name || '');
      formData.append('insuranceDocUrl', files.insuranceDocUrl || new Blob(), files.insuranceDocUrl?.name || '');
      formData.append('roadTaxDocUrl', files.roadTaxDocUrl || new Blob(), files.roadTaxDocUrl?.name || '');

      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/update-vehicle-documents/${driverId}`,
        formData
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to upload banking and payout info (POST)
 * @param options - Hook options
 */
export function useUploadBankingInfo(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        bankAccountHolderName: string;
        bankName: string;
        accountNumber: string;
        ifscCode: string;
        swiftCode: string;
        eWalletId: string;
        paymentFrequency: string;
      }
    ) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({
      driverId,
      bankAccountHolderName,
      bankName,
      accountNumber,
      ifscCode,
      swiftCode,
      eWalletId,
      paymentFrequency,
    }) => {
      const queryParams = {
        driverId,
        bankAccountHolderName,
        bankName,
        accountNumber,
        ifscCode,
        swiftCode,
        eWalletId,
        paymentFrequency,
      };

      const response = await adminApiClient.post<ApiResponse<DriverDTO>>(
        '/drivers/banking-and-payout-info',
        null,
        { params: queryParams }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update banking and payout info (PUT)
 * @param options - Hook options
 */
export function useUpdateBankingInfo(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        bankAccountHolderName: string;
        bankName: string;
        accountNumber: string;
        ifscCode: string;
        swiftCode: string;
        eWalletId: string;
        paymentFrequency: string;
      }
    ) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({
      driverId,
      bankAccountHolderName,
      bankName,
      accountNumber,
      ifscCode,
      swiftCode,
      eWalletId,
      paymentFrequency,
    }) => {
      const queryParams = {
        bankAccountHolderName,
        bankName,
        accountNumber,
        ifscCode,
        swiftCode,
        eWalletId,
        paymentFrequency,
      };

      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/update-banking-and-payout-info/${driverId}`,
        null,
        { params: queryParams }
      );
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['driver', variables.driverId] });
      queryClient.invalidateQueries({ queryKey: ['drivers'], exact: false });
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
}

/**
 * Hook to update personal details
 * @param options - Hook options
 */
export function useUpdatePersonalDetails(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: { id: number; data: DriverDTO }
    ) => void;
    onError?: (error: Error, variables: { id: number; data: DriverDTO }) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<DriverDTO>, Error, { id: number; data: DriverDTO }>({
    mutationFn: async ({ id, data }) => {
      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/update-personal-details/${id}`,
        data
      );
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['driver', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['drivers'], exact: false });
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
}

/**
 * Hook to approve KYC documents
 * @param options - Hook options
 */
export function useApproveKyc(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<DriverDTO>, Error, number>({
    mutationFn: async (driverId: number) => {
      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/approve-kyc-documents/${driverId}`
      );
      return response;
    },
    onSuccess: (data, driverId) => {
      queryClient.invalidateQueries({ queryKey: ['driver', driverId] });
      queryClient.invalidateQueries({ queryKey: ['drivers'], exact: false });
      options.onSuccess?.(data, driverId);
    },
    onError: options.onError,
  });
}

/**
 * Hook to reject KYC documents
 * @param options - Hook options
 */
export function useRejectKyc(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: { driverId: number; rejectionReason: string }
    ) => void;
    onError?: (
      error: Error,
      variables: { driverId: number; rejectionReason: string }
    ) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<DriverDTO>, Error, { driverId: number; rejectionReason: string }>({
    mutationFn: async ({ driverId, rejectionReason }) => {
      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/reject-kyc-documents/${driverId}`,
        null,
        { params: { rejectionReason } }
      );
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['driver', variables.driverId] });
      queryClient.invalidateQueries({ queryKey: ['drivers'], exact: false });
      options.onSuccess?.(data, variables);
    },
    onError: options.onError,
  });
}

/**
 * Hook to keep KYC documents in view
 * @param options - Hook options
 */
export function useKeepInViewKyc(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<DriverDTO>, Error, number>({
    mutationFn: async (driverId: number) => {
      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/keepinview-kyc-documents/${driverId}`
      );
      return response;
    },
    onSuccess: (data, driverId) => {
      queryClient.invalidateQueries({ queryKey: ['driver', driverId] });
      queryClient.invalidateQueries({ queryKey: ['drivers'], exact: false });
      options.onSuccess?.(data, driverId);
    },
    onError: options.onError,
  });
}

/**
 * Hook to download driver document
 * @param options - Hook options
 */
export function useDownloadDriverDocument(
  options: {
    onSuccess?: (data: Blob, variables: string) => void;
    onError?: (error: Error, variables: string) => void;
  } = {}
) {
  return useMutation<Blob, Error, string>({
    mutationFn: async (filePath: string) => {
      const response = await adminApiClient.get<Blob>(
        '/drivers/driver-document-download',
        { filePath },
        { responseType: 'blob' }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Fetch a temporary (pre-signed) HTTP URL for a driver document.
 * Endpoint: GET /drivers/document/temp-url?filePath=<path>
 * Returns a plain URL string that can be used in <img> or for download.
 */
export async function getDocumentTempUrl(filePath: string): Promise<string> {
  const response = await adminApiClient.get<{ url: string } | string>(
    '/drivers/document/temp-url',
    { filePath }
  );
  // The API may return { url: "..." } or a plain string
  if (typeof response === 'string') return response;
  if (response && typeof response === 'object' && 'url' in response) return response.url;
  throw new Error('Invalid response from temp-url endpoint');
}

/**
 * Hook to fetch temp URLs for an array of document file paths.
 * Returns a map of filePath → resolved HTTP URL.
 */
export function useDocumentTempUrls(filePaths: string[]) {
  const [urlMap, setUrlMap] = React.useState<Record<string, string>>({});
  const [loading, setLoading] = React.useState(false);

  // Stable serialised key so the effect only re-runs when paths actually change
  const pathsKey = filePaths.filter(Boolean).sort().join('|');

  React.useEffect(() => {
    const paths = pathsKey.split('|').filter(Boolean);
    if (paths.length === 0) {
      setUrlMap({});
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all(
      paths.map(async (fp) => {
        try {
          const url = await getDocumentTempUrl(fp);
          return [fp, url] as const;
        } catch {
          console.warn('Failed to get temp URL for', fp);
          return [fp, ''] as const;
        }
      })
    ).then((entries) => {
      if (!cancelled) {
        setUrlMap(Object.fromEntries(entries));
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [pathsKey]);

  return { urlMap, loading };
}
